import React from "react";
import { Text } from "web/react/emo/text";

import SVGIcon, { SVGIconName } from "web/react/components/svg-icon/svg-icon";
import { TextStyle } from "web/react/emo/sprinkles.css";
import environment from "web/script/modules/environment";
import { buttonRecipe, fullWidth, iconRecipe } from "./button.css";

type IconType = "app" | "apple" | "facebook" | "google";

export interface ButtonProps {
    size?: "sm" | "lg";
    width?: "full" | "fit-content";
    icon?: IconType;
    variant?: "primary" | "secondary";
    disabled?: boolean;
    href?: string;
    onClick?: React.MouseEventHandler<HTMLElement>;
    title: string;
    type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
}

export function Button({
    size = "lg",
    width = "fit-content",
    icon,
    variant = "primary",
    disabled = false,
    href,
    onClick,
    title,
    type = "button",
}: ButtonProps): React.ReactElement {
    const textStyles: { [key: string]: TextStyle } = {
        lg: "callout",
        sm: "caption-1",
    };

    const icons: { readonly [i in IconType]: { src: SVGIconName; alt: string } } = {
        app: { src: "lyst-app-icon", alt: "Lyst app icon" },
        apple: { src: "apple-icon", alt: "Apple icon" },
        facebook: { src: "facebook-icon", alt: "Facebook icon" },
        google: { src: "google-icon", alt: "Google icon" },
    };

    const showIcon = icon && size !== "sm";

    const useNewButtonAnimation = !!environment.getFeature("eac_new_button_animations");

    const buttonClass = buttonRecipe({
        size,
        width,
        disabled,
        variant: icon ? "secondary" : variant,
        icon: showIcon,
        useNewButtonAnimation,
    });

    const content = (
        <>
            {showIcon && (
                <SVGIcon
                    className={iconRecipe({
                        width: width,
                        background: icon === "app",
                    })}
                    name={icons[icon]["src"]}
                    overrideStyle
                />
            )}
            <Text
                as="span"
                className={width === "full" && showIcon ? fullWidth : undefined}
                textStyle={textStyles[size]}
                color={"inherit"}
            >
                {title}
            </Text>
        </>
    );

    return href ? (
        <a className={buttonClass} onClick={onClick} href={href}>
            {content}
        </a>
    ) : (
        <button className={buttonClass} onClick={onClick} disabled={disabled} type={type}>
            {content}
        </button>
    );
}
